import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Section, Container, SectionTitle } from '../styles/StyledComponents';

const ExperienceContainer = styled(Container)`
  position: relative;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: 50%;
    width: 2px;
    background-color: var(--light-blue);
    transform: translateX(-50%);
    
    @media (max-width: 768px) {
      left: 30px;
    }
  }
`;

const ExperienceItem = styled(motion.div)`
  display: flex;
  margin-bottom: 4rem;
  
  &:last-child {
    margin-bottom: 0;
  }
  
  &:nth-child(even) {
    flex-direction: row-reverse;
    
    @media (max-width: 768px) {
      flex-direction: row;
    }
  }
  
  @media (max-width: 768px) {
    margin-bottom: 3rem;
  }
`;

const ExperienceContent = styled.div`
  width: calc(50% - 30px);
  padding: 2rem;
  background-color: var(--white);
  border-radius: 8px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.05);
  position: relative;
  
  &:before {
    content: '';
    position: absolute;
    top: 30px;
    width: 20px;
    height: 20px;
    background-color: var(--white);
    transform: rotate(45deg);
  }
  
  ${ExperienceItem}:nth-child(odd) & {
    &:before {
      right: -10px;
    }
  }
  
  ${ExperienceItem}:nth-child(even) & {
    &:before {
      left: -10px;
    }
  }
  
  @media (max-width: 768px) {
    width: calc(100% - 80px);
    margin-left: 80px;
    
    &:before {
      left: -10px !important;
    }
  }
`;

const TimelineDot = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background-color: var(--primary-blue);
  border: 4px solid var(--light-blue);
  position: absolute;
  top: 30px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 2;
  
  @media (max-width: 768px) {
    left: 30px;
  }
`;

const CompanyName = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 0.5rem;
  color: var(--dark-blue);
`;

const Position = styled.h4`
  font-size: 1.2rem;
  color: var(--primary-blue);
  margin-bottom: 1rem;
`;

const Description = styled.p`
  color: var(--medium-gray);
  line-height: 1.8;
`;

const Experience: React.FC = () => {
  const experienceData = [
    {
      company: "Rose Pharmacy",
      position: "Pharmacist",
      description: "At Rose Pharmacy, I meticulously oversaw the completion of prescriptions and provided comprehensive medication counseling to patients, ensuring a 98 percent accuracy rate while upholding strict FDA and DOH standards. Additionally, I educated patients on drug interactions and proper dosing for chronic conditions, fostering trust and promoting better health outcomes."
    },
    {
      company: "Ultramart",
      position: "Branch Manager/Pharmacist",
      description: "At Ultramart, I successfully balanced dual responsibilities as Branch Manager and Pharmacist, overseeing pharmacy operations while leading a team of five staff to ensure seamless integration of retail and healthcare services. My leadership drove efficiency improvements by blending pharmacy workflows with front-end operations, reducing inventory surplus by 15 percent and maintaining an exceptional 98 percent accuracy rate in medication dispensing."
    },
    {
      company: "Wellchoice Pharmacy",
      position: "Business Owner",
      description: "At Wellchoice Pharmacy, I successfully led pharmacy operations while managing a retail convenience store, demonstrating expertise in medication dispensing, inventory control, and regulatory compliance alongside business management. By integrating patient care with strategic business oversight, I optimized workflows, resolved complex operational challenges, and ensured seamless customer and patient satisfaction, all while maintaining accuracy in medication dispensation and fostering a culture of trust and efficiency."
    }
  ];

  return (
    <Section id="experience">
      <Container>
        <SectionTitle
          as={motion.h2}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          Work Experience
        </SectionTitle>
        
        <ExperienceContainer>
          {experienceData.map((exp, index) => (
            <ExperienceItem
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.2 }}
            >
              <TimelineDot />
              <ExperienceContent>
                <CompanyName>{exp.company}</CompanyName>
                <Position>{exp.position}</Position>
                <Description>{exp.description}</Description>
              </ExperienceContent>
            </ExperienceItem>
          ))}
        </ExperienceContainer>
      </Container>
    </Section>
  );
};

export default Experience;
